/**
 * Bullet rows: bar = actual, tick = full budget, both on a shared scale. Used for the overview
 * department ranking and the department category breakdown.
 */
import { type ReactNode, useCallback } from 'react'

import { formatMoney } from '@/lib/format'

import type { PaceStatus } from '../lib/pace'
import styles from './BulletRows.module.css'

export interface BulletRowData {
  key: string
  name: string
  actual: number
  budget: number
  status: PaceStatus
  figures: ReactNode
}

interface BulletRowsProps {
  rows: BulletRowData[]
  onActivate?: (key: string) => void
  /** Accessible label for a clickable row. */
  actionLabel?: (row: BulletRowData) => string
}

const FILL: Record<PaceStatus, string> = {
  good: styles.fillGood ?? '',
  bad: styles.fillBad ?? '',
  neutral: styles.fillNeutral ?? '',
}

function BulletTrack({
  fillPct,
  markPct,
  budget,
  status,
}: {
  fillPct: number
  markPct: number
  budget: number
  status: PaceStatus
}) {
  return (
    <svg className={styles.track} aria-hidden="true" focusable="false">
      <rect className={FILL[status]} x="0" y="0" width={`${fillPct}%`} height="100%" rx="3" />
      <line className={styles.mark} x1={`${markPct}%`} x2={`${markPct}%`} y1="-3" y2="19">
        <title>{`Budget ${formatMoney(budget)}`}</title>
      </line>
    </svg>
  )
}

function BulletRow({
  row,
  maxVal,
  onActivate,
  actionLabel,
}: {
  row: BulletRowData
  maxVal: number
  onActivate?: (key: string) => void
  actionLabel?: (row: BulletRowData) => string
}) {
  const handleClick = useCallback(() => onActivate?.(row.key), [onActivate, row.key])
  const fillPct = Math.max(0, Math.min(100, (row.actual / maxVal) * 100))
  const markPct = Math.max(0, Math.min(100, (row.budget / maxVal) * 100))
  const content = (
    <>
      <span className={styles.name} title={row.name}>
        {row.name}
      </span>
      <BulletTrack fillPct={fillPct} markPct={markPct} budget={row.budget} status={row.status} />
      <span className={`${styles.figures} num`}>{row.figures}</span>
    </>
  )
  if (!onActivate) return <div className={styles.row}>{content}</div>
  return (
    <button type="button" className={styles.row} onClick={handleClick} aria-label={actionLabel?.(row)}>
      {content}
    </button>
  )
}

export function BulletRows({ rows, onActivate, actionLabel }: BulletRowsProps) {
  const maxVal = Math.max(1, ...rows.map((r) => Math.max(r.actual, r.budget)))
  return (
    <ul className={styles.rows}>
      {rows.map((row) => (
        <li key={row.key} className={styles.item}>
          <BulletRow row={row} maxVal={maxVal} onActivate={onActivate} actionLabel={actionLabel} />
        </li>
      ))}
    </ul>
  )
}
